import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import * as log from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy, MailPlus, Check, AlertTriangle, RefreshCw, Send, RotateCw } from "lucide-react";
import { toast } from "sonner";

/**
 * Platform Admin — Waitlist Leads.
 *
 * Only reachable by owners/admins of the internal-workspace org. The backend
 * returns 403 if the caller is not a platform admin.
 */
const STATUSES = ["waiting", "contacted", "invited", "approved", "declined"];
const STATUS_STYLE = {
  waiting:   "bg-amber-100 text-amber-800",
  contacted: "bg-sky-100 text-sky-800",
  invited:   "bg-indigo-100 text-indigo-800",
  approved:  "bg-emerald-100 text-emerald-800",
  declined:  "bg-zinc-200 text-zinc-600 line-through",
};

const EMAIL_STATUS_STYLE = {
  sent:              "text-emerald-700",
  not_attempted:     "text-amber-700",
  not_configured:    "text-amber-700", // legacy vocabulary — same style
  pending:           "text-zinc-500",
  failed:            "text-red-700",
  skipped_migration: "text-zinc-400",
};

export default function WaitlistLeadsView() {
  const [rows, setRows] = useState(null);
  const [gate, setGate] = useState(null);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [diagnostics, setDiagnostics] = useState(null);

  const load = async () => {
    try {
      const params = {};
      if (status !== "all") params.status = status;
      if (q.trim()) params.q = q.trim();
      const [r, d] = await Promise.all([
        api.get("/platform/waitlist-leads", { params }),
        api.get("/platform/diagnostics").catch(() => ({ data: null })),
      ]);
      setRows(r.data);
      setDiagnostics(d?.data || null);
    } catch (e) {
      if (e.response?.status === 403) setGate("You need platform-admin access to view this page.");
      else setGate("Could not load waitlist leads.");
    }
  };

  useEffect(() => { load(); }, [status]);

  const filtered = useMemo(() => rows || [], [rows]);

  const changeStatus = async (id, newStatus) => {
    try {
      await api.patch(`/platform/waitlist-leads/${id}`, { status: newStatus });
      toast.success(`Marked as ${newStatus}`);
      load();
    } catch { toast.error("Failed"); }
  };

  const retryEmail = async (id, which) => {
    try {
      const { data } = await api.post(`/platform/waitlist-leads/${id}/retry-${which}`);
      const st = which === "admin" ? data.admin_notification_status : data.applicant_confirmation_status;
      if (st === "sent") toast.success(`Retry ${which}: sent`);
      else if (st === "not_attempted") toast.warning(`Retry ${which}: not attempted (Resend not configured)`);
      else toast.error(`Retry ${which}: ${st}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Retry ${which} failed`);
    }
  };

  const copyEmail = async (email, id) => {
    try {
      await navigator.clipboard.writeText(email);
    } catch (err) {
      // Clipboard API can be blocked on non-HTTPS pages, old browsers, or
      // permission-denied contexts. The user still gets the success toast
      // (the copy silently no-ops), and we log at debug level for triage.
      log.debug("[waitlist] clipboard write failed", err);
    }
    setCopiedId(id);
    toast.success("Email copied.");
    setTimeout(() => setCopiedId(null), 1200);
  };

  if (gate) {
    return (
      <div className="flex-1 flex items-center justify-center p-10" data-testid="waitlist-gate">
        <div className="text-center max-w-sm">
          <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto" />
          <div className="mt-3 text-lg font-semibold">Restricted</div>
          <div className="text-sm text-zinc-600 mt-1">{gate}</div>
        </div>
      </div>
    );
  }

  if (rows === null) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white" data-testid="waitlist-view">
      <div className="h-14 border-b flex items-center px-6 shrink-0 gap-3">
        <h1 className="text-lg font-semibold">Waitlist leads</h1>
        <span className="text-[10px] uppercase tracking-widest text-zinc-500 border rounded-full px-2 py-0.5">Platform admin</span>
        <div className="ml-auto flex items-center gap-2">
          <Input placeholder="Search email / name / company…" value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") load(); }}
            className="w-64 h-9" data-testid="waitlist-search"/>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-36 h-9" data-testid="waitlist-status-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={load} data-testid="waitlist-refresh">
            <RefreshCw className="w-4 h-4 mr-1"/> Refresh
          </Button>
        </div>
      </div>

      {diagnostics && (
        <div className="border-b bg-zinc-50 px-6 py-2 text-[11px] text-zinc-600 flex flex-wrap gap-x-4 gap-y-1" data-testid="waitlist-diagnostics">
          <span>App URL: <b className="mono">{diagnostics.app_url || "—"}</b></span>
          <span>Deployment: <b>{diagnostics.deployment_env}</b></span>
          <span>Resend: <b className={diagnostics.resend_configured ? "text-emerald-700" : "text-red-700"}>{diagnostics.resend_configured ? "configured" : "not configured"}</b></span>
          <span>Waitlist recipient: <b className={diagnostics.waitlist_notify_configured ? "text-emerald-700" : "text-red-700"}>{diagnostics.waitlist_notify_configured ? "configured" : "missing"}</b></span>
          <span>Invite sender: <b className={diagnostics.invite_sender_configured ? "text-emerald-700" : "text-red-700"}>{diagnostics.invite_sender_configured ? "configured" : "missing"}</b></span>
          <span>Beta access: <b>{diagnostics.beta_access_enabled ? "on" : "off"}</b>{diagnostics.beta_access_enabled ? ` · domains: ${(diagnostics.allowed_email_domains || []).join(", ") || "—"}` : ""}</span>
        </div>
      )}

      <div className="flex-1 overflow-auto scroll-thin">
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-zinc-500" data-testid="waitlist-empty">No waitlist leads yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-widest text-zinc-500 border-b sticky top-0 bg-white">
              <tr>
                <th className="px-4 py-2">Company / Name</th>
                <th className="px-2 py-2">Work email</th>
                <th className="px-2 py-2">Team size</th>
                <th className="px-2 py-2">Submitted</th>
                <th className="px-2 py-2">Status</th>
                <th className="px-2 py-2">Emails</th>
                <th className="px-2 py-2 text-right pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-b hover:bg-zinc-50" data-testid={`waitlist-row-${r.id}`}>
                  <td className="px-4 py-2">
                    <div className="font-medium">{r.company || r.name || "—"}</div>
                    <div className="text-[11px] text-zinc-500 truncate max-w-[220px]">{r.intended_use || (r.notes || "")}</div>
                  </td>
                  <td className="px-2 py-2 mono text-[12px]" data-testid={`waitlist-email-${r.id}`}>{r.email}</td>
                  <td className="px-2 py-2 text-[12px]">{r.team_size || "—"}</td>
                  <td className="px-2 py-2 text-[12px] text-zinc-500">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>
                  <td className="px-2 py-2">
                    <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full ${STATUS_STYLE[r.status] || "bg-zinc-100"}`}>{r.status || "waiting"}</span>
                  </td>
                  <td className="px-2 py-2 text-[11px]">
                    <div className={`flex items-center gap-1 ${EMAIL_STATUS_STYLE[r.admin_notification_status] || "text-zinc-500"}`}>
                      <span className="whitespace-nowrap">admin: {r.admin_notification_status || "—"}</span>
                      {["failed", "not_attempted", "not_configured"].includes(r.admin_notification_status) && (
                        <button
                          type="button"
                          onClick={() => retryEmail(r.id, "admin")}
                          className="ml-1 text-[10px] font-semibold text-[var(--brand-accent)] hover:underline"
                          data-testid={`waitlist-retry-admin-${r.id}`}
                          title="Retry admin notification"
                        ><RotateCw className="w-3 h-3 inline"/> Retry</button>
                      )}
                    </div>
                    <div className={`flex items-center gap-1 ${EMAIL_STATUS_STYLE[r.applicant_confirmation_status] || "text-zinc-500"}`}>
                      <span className="whitespace-nowrap">applicant: {r.applicant_confirmation_status || "—"}</span>
                      {["failed", "not_attempted", "not_configured"].includes(r.applicant_confirmation_status) && (
                        <button
                          type="button"
                          onClick={() => retryEmail(r.id, "applicant")}
                          className="ml-1 text-[10px] font-semibold text-[var(--brand-accent)] hover:underline"
                          data-testid={`waitlist-retry-applicant-${r.id}`}
                          title="Retry applicant confirmation"
                        ><Send className="w-3 h-3 inline"/> Retry</button>
                      )}
                    </div>
                    {(r.notification_attempt_count || 0) > 0 && (
                      <div className="text-[10px] text-zinc-400 mt-0.5" data-testid={`waitlist-attempts-${r.id}`}>
                        {r.notification_attempt_count} attempt{r.notification_attempt_count > 1 ? "s" : ""}
                      </div>
                    )}
                    {(r.admin_email_error || r.applicant_email_error) && (
                      <div className="text-[10px] text-red-600 mt-0.5 truncate max-w-[200px]" title={r.admin_email_error || r.applicant_email_error}>
                        err: {(r.admin_email_error || r.applicant_email_error || "").slice(0, 40)}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-2 text-right pr-4">
                    <div className="inline-flex items-center gap-1">
                      <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => copyEmail(r.email, r.id)} data-testid={`waitlist-copy-${r.id}`}>
                        {copiedId === r.id ? <Check className="w-3.5 h-3.5"/> : <Copy className="w-3.5 h-3.5"/>}
                      </Button>
                      <Select value={r.status || "waiting"} onValueChange={(v) => changeStatus(r.id, v)}>
                        <SelectTrigger className="h-7 text-xs w-32" data-testid={`waitlist-status-${r.id}`}><SelectValue/></SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
